import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Container,
  Grid,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormLabel,
  TextField,
  Typography,
  Select,
  InputLabel,
  FormControl,
  CircularProgress,
} from "@mui/material";
import instance from "../utils/axiosInstance";
import { useNavigate } from "react-router-dom";

const NFTCreate = ({ user }) => {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [image, setImage] = useState(null);
  const [preview, setPreview] = useState(null);
  const [mintType, setMintType] = useState("lazy");
  const [price, setPrice] = useState("");
  const [wallets, setWallets] = useState([]);
  const [selectedWallet, setSelectedWallet] = useState("");
  const [password, setPassword] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [creator, setCreator] = useState("");

  const navigate = useNavigate();
  const NFT_CONTRACT_ADDRESS = process.env.REACT_APP_NFT_CONTRACT_ADDRESS;

  useEffect(() => {
    if (user) {
      instance
        .get("/wallet/me", { withCredentials: true })
        .then((res) => {
          setWallets(res.data.wallets || []);
          if (res.data.wallets.length > 0) {
            setSelectedWallet(res.data.wallets[0].address);
          }
        })
        .catch((err) => {
          console.error("지갑 불러오기 실패:", err);
        });
    }

    const fetchUser = async () => {
      try {
        const res = await instance.get("/user/me", { withCredentials: true });
      } catch (error) {
        navigate("/login");
      }
    };

    fetchUser();
  }, [user]);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    setImage(file);
    setPreview(URL.createObjectURL(file));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!user) return alert("로그인이 필요합니다.");
    if (!image || !name || !description || !selectedWallet || !password) {
      return alert("모든 항목을 입력해주세요.");
    }
    if (mintType === "lazy" && !price) {
      return alert("판매 가격을 입력해주세요.");
    }

    setIsSubmitting(true);
    const formData = new FormData();
    formData.append("name", name);
    formData.append("creator", creator.trim() || user.name);
    formData.append("description", description);
    formData.append("image", image);
    formData.append("mintType", mintType);
    formData.append("walletAddress", selectedWallet);
    formData.append("password", password);
    formData.append("nftAddress", NFT_CONTRACT_ADDRESS);
    if (mintType === "lazy") formData.append("price", price);

    try {
      const res = await instance.post("/nftmanage/create", formData, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      });
    
      if (mintType === "lazy") {
        alert("NFT 생성 완료! (Lazy Minting 방식으로 판매 등록됨)");
      } else {
        alert("NFT 생성 완료! (온체인 방식으로 민팅됨)");
      }
    
      navigate(`/nft/${res.data.nftId}`);
    } catch (err) {
      console.error("NFT 생성 실패:", err);
      alert(err.response?.data?.message || "NFT 생성 중 오류 발생");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file && file.type.startsWith("image/")) {
      setImage(file);
      setPreview(URL.createObjectURL(file));
    } else {
      alert("이미지 파일만 업로드할 수 있습니다.");
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  return (
    <Container maxWidth="md" sx={{ py: 6 }}>
      <Paper
        elevation={4}
        sx={(theme) => ({
          p: 4,
          borderRadius: 4,
          backgroundColor: theme.palette.background.paper,
          color: theme.palette.text.primary,
        })}
      >
        <form onSubmit={handleSubmit}>
          <Grid container spacing={4}>
            {/* 이미지 업로드 */}
            <Grid item xs={12} md={6}>
            <Paper
              component="label" // 여기 핵심!!
              elevation={1}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              sx={(theme) => ({
                height: 300,
                border: "2px dashed #aaa",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                backgroundColor: theme.palette.background.default,
                borderRadius: 2,
                textAlign: "center",
                overflow: "hidden",
                cursor: "pointer",
                position: "relative", // 자식으로 input 배치 가능하게
              })}
            >
              {Boolean(preview && typeof preview === "string") ? (
                <img
                  src={preview}
                  alt="미리보기"
                  style={{ maxWidth: "100%", maxHeight: "100%", borderRadius: 12 }}
                />
              ) : (
                <>
                  <Typography variant="h6" gutterBottom>
                    미디어 파일을 드래그하거나 클릭하세요
                  </Typography>
                  <Typography variant="caption" sx={{ mt: 1 }} color="text.secondary">
                    최대 50MB | JPG, PNG, GIF, SVG, MP4
                  </Typography>
                </>
              )}

              {/* 파일 input은 영역 전체와 연결 */}
              <input
                type="file"
                hidden
                accept="image/*"
                onChange={handleImageChange}
              />
            </Paper>

            </Grid>

            {/* 정보 입력 */}
            <Grid item xs={12} md={6} display="flex" flexDirection="column" gap={2}>
              <TextField
                label="제목 (작품 이름)"
                variant="outlined"
                fullWidth
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />

              <TextField
                label="작가 이름 (표기될 이름)"
                variant="outlined"
                fullWidth
                value={creator}
                onChange={(e) => setCreator(e.target.value)}
                placeholder={user?.name || "사용자 닉네임"}
              />

              <TextField
                label="설명 (작품 설명)"
                variant="outlined"
                multiline
                rows={4}
                fullWidth
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                required
              />

              <TextField
                label="가격 (ETH)"
                type="number"
                variant="outlined"
                fullWidth
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                required={mintType === "lazy"}
                disabled={mintType !== "lazy"}
                InputProps={{
                  readOnly: mintType !== "lazy",
                }}
              />

              <FormControl component="fieldset">
                <FormLabel>민팅 방식</FormLabel>
                <RadioGroup
                  row
                  value={mintType}
                  onChange={(e) => setMintType(e.target.value)}
                >
                  <FormControlLabel value="lazy" control={<Radio />} label="Lazy Minting" />
                  <FormControlLabel value="onchain" control={<Radio />} label="온체인 민팅" />
                </RadioGroup>
              </FormControl>

              <FormControl fullWidth>
                <InputLabel>지갑 선택</InputLabel>
                <Select
                  value={selectedWallet}
                  onChange={(e) => setSelectedWallet(e.target.value)}
                  required
                >
                  {wallets.map((wallet) => (
                    <MenuItem key={wallet.address} value={wallet.address}>
                      {wallet.address}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                label="지갑 비밀번호"
                type="password"
                variant="outlined"
                fullWidth
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </Grid>
          </Grid>

          {/* 제출 버튼 */}
          <Box mt={4} display="flex" justifyContent="flex-end">
            <Button
              variant="contained"
              type="submit"
              disabled={isSubmitting}
              startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : null}
            >
              {isSubmitting ? "생성 중..." : "NFT 생성"}
            </Button>
          </Box>
        </form>
      </Paper>
    </Container>
  );
};

export default NFTCreate;
